import { useState, useEffect, useCallback } from "react";
import ScrollReveal from "@/components/base/ScrollReveal";
import { api, resolveAssetUrl } from "@/lib/api";
import type { ProductItem, ProductCategory } from "@/lib/types";
import { SITE_IMAGES } from "@/lib/siteAssets";

const tabMeta: Record<string, { label: string; color: string; title: string; desc: string }> = {
  pharma: {
    label: "Pharma",
    color: "bg-[#e67e22]",
    title: "Novel Target and Lead Packages for Diverse Indications",
    desc: "To facilitate the rapid progression of novel new drugs into clinical trials, our group focuses on new mechanistic studies and lead compound development necessary for the initial phases of drug development. We work on key facets of drug development that include mechanistic studies disease indications, clinical validations, structural analyses, and biomarkers for lead compound development.",
  },
  tech: {
    label: "Tech",
    color: "bg-[#2ecc71]",
    title: "Devices, Kits and Instruments with Smart, Accurate, Fast, Economic Technologies",
    desc: "A key element of biotechnology development is the development of technology to increase the productivity of drug development. Among our product offerings, we have developed instruments can accurately analyze protein interactions, that facilitate antibody development, provide live imaging of disease detection and offer 3D culture analysis. We have additional capacity in new biochip products.",
  },
  service: {
    label: "Service",
    color: "bg-[#3498db]",
    title: "One Stop Solution for Experimental Needs",
    desc: 'Biocon is committed to assimilating institutional infrastructure and knowhow so that we can effectively support and service Korean research groups or companies in new drug research. A key Biocon offering is a "One Stop Solution" that offers a broad selection of key drug development services, including the creation of novel proteins and antibodies, protein purification, dielectric, proteomics and 2D and 3D cell culture. We offer additional services in genetically modified animal models, drug potency checks of patient samples using mouse, bioanalysis, analysis of molecular binding using SPR. Finally, we can offer Biobank and inventory services.',
  },
};

export default function Product() {
  const [activeTab, setActiveTab] = useState("pharma");
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState<ProductItem[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, itemRes] = await Promise.all([
        api.get<ProductCategory[]>("/public/products/categories"),
        api.get<ProductItem[]>("/public/products"),
      ]);
      if (catRes.data) setCategories(catRes.data);
      if (itemRes.data) setItems(itemRes.data);
    } catch {
      // 로딩 실패 시 빈 목록 유지
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeMeta = tabMeta[activeTab];
  const activeCategory = categories.find((c) => c.name.toLowerCase() === activeTab);

  const filteredItems = items.filter((it) => {
    const matchesTab = activeCategory ? it.categoryId === activeCategory.id : false;
    const matchesSearch = it.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white pt-[96px]">
      {/* Hero Banner */}
      <section className="relative h-[260px] md:h-[320px] overflow-hidden">
        <img
          src={SITE_IMAGES.productBanner}
          alt="Product"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-3xl md:text-5xl font-semibold text-white tracking-wide">Product</h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-[#f6f9fc] border-b border-gray-100">
        <div className="container mx-auto px-4 py-3">
          <nav className="text-sm text-gray-500">
            <a href="/" className="hover:text-[#1977cc] transition-colors">Home</a>
            <span className="mx-2 text-gray-300">/</span>
            <span className="text-[#2c4964] font-medium">Product</span>
          </nav>
        </div>
      </div>

      {/* Tabs */}
      <section className="bg-[#f6f9fc] pt-8 pb-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {Object.entries(tabMeta).map(([id, meta]) => (
              <button
                key={id}
                onClick={() => { setActiveTab(id); setSearchTerm(""); }}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === id
                    ? `${meta.color} text-white shadow-sm`
                    : "bg-white text-[#2c4964] hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {meta.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-10 md:py-14">
        <div className="container mx-auto px-4">
          {/* Category Header */}
          <ScrollReveal>
            <div className="mb-10">
              <div className={`inline-block px-4 py-2 ${activeMeta.color} rounded text-white text-lg font-semibold mb-6`}>
                {activeMeta.label}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-[#2c4964] leading-snug">{activeMeta.title}</h2>
                </div>
                <div>
                  <p className="text-sm text-gray-600 leading-relaxed">{activeMeta.desc}</p>
                </div>
              </div>

              {/* Search */}
              <div className="flex items-center gap-2 mb-6 max-w-md ml-auto">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
                  />
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Cards Grid */}
          <ScrollReveal delay={0.1}>
            {loading ? (
              <div className="text-center py-12 text-gray-400">Loading...</div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No matching items found.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredItems.map((item) => {
                    const imageUrl = resolveAssetUrl(
                      item.media && item.media.length > 0 ? item.media[0].url : ""
                    );
                    const pdfUrl = resolveAssetUrl(item.pdfUrl);

                  return (
                    <div
                      key={item.id}
                      className="group bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200 overflow-hidden"
                    >
                      <div className="relative h-48 bg-[#f6f9fc] overflow-hidden">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.name}
                            className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <i className="ri-image-line text-3xl" />
                          </div>
                        )}
                      </div>
                      <div className="p-5">
                        <h3 className="text-sm font-semibold text-[#2c4964] leading-snug mb-3 line-clamp-2">
                          {item.name}
                        </h3>
                        <div className="flex items-center justify-between">
                          {pdfUrl ? (
                            <a
                              href={pdfUrl}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[#1977cc] hover:text-[#1669b3] text-xs font-medium transition-colors"
                            >
                              <i className="ri-file-pdf-line" />
                              PDF Download
                            </a>
                          ) : (
                            <span />
                          )}
                          {item.info && (
                            <span className="text-xs text-gray-400">{item.info}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}